import React, { useLayoutEffect, useState } from "react";

interface IndicatorState {
  left: number;
  width: number;
  ready: boolean;
}

interface UseAnimatedIndicatorOptions {
  /**
   * Function that returns the active element to track.
   * Should return null if the element is not yet available.
   */
  getActiveElement: () => HTMLElement | null;
  /**
   * Additional dependencies that should trigger indicator recalculation
   */
  deps?: React.DependencyList;
}

/**
 * Hook for managing an animated indicator that tracks the position and size
 * of an active element within a container. Used by AnimatedTabs and DateRangeTabs
 * to create a sliding pill indicator effect.
 *
 * @param containerRef - Ref to the container element
 * @param options - Configuration options
 * @returns The indicator state with left, width, and ready properties
 */
export function useAnimatedIndicator(
  containerRef: React.RefObject<HTMLElement | null>,
  options: UseAnimatedIndicatorOptions,
): IndicatorState {
  const { getActiveElement, deps = [] } = options;
  const [indicator, setIndicator] = useState<IndicatorState>({
    left: 0,
    width: 0,
    ready: false,
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateIndicator = () => {
      const activeElement = getActiveElement();
      if (!activeElement) {
        return;
      }

      setIndicator({
        left: activeElement.offsetLeft,
        width: activeElement.offsetWidth,
        ready: true,
      });
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      updateIndicator();
    });

    // Watch for container size changes (e.g., window resize, content changes)
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            updateIndicator();
          })
        : null;

    resizeObserver?.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, getActiveElement, ...deps]);

  return indicator;
}
